import { sendStreamingChatMessage, CHAT_API_BASE_URL } from "../../services/chatService";
import type { ChatStreamError } from "../../services/chatService";
import { handleStreamEvent } from "../../components/AIAgents/ChatWithAgent/utils/streamHandler";
import { authedFetch } from "../../auth/authedFetch";
import { debug } from "../../utils/debugLogger";

export interface OnboardingChatMessage {
  role: "user" | "assistant";
  content: string;
}

/** Matches the onboarding confirmation card + brand wizard seed fields. */
export interface OnboardingBusinessProfile {
  business_name: string;
  website_url: string;
  city: string;
  state: string;
  country: string;
  industry: string;
  brand_voice: string;
  services: string[];
  description: string;
  message: string;
  // Firecrawl-enriched fields
  tagline?: string;
  business_type?: "SERVICE" | "PRODUCT" | "BOTH" | "";
  target_audience?: string;
  service_areas?: string[];
  phone_number?: string;
  contact_email?: string;
  manager_email?: string;
  // Brand identity from website
  brand_colors?: string[];
  // The same colours with the role each one plays, as measured (SGL-223).
  // `brand_colors` is the flat list kept for older consumers; this is the one to
  // read, because a text colour dropped into a "secondary" slot is no longer a
  // text colour to anything downstream.
  brand_palette?: MeasuredBrandColor[];
  // True when brand_colors were measured from a screenshot of the customer's site
  // rather than supplied by them (SGL-223). Drives the "detected, please check"
  // notice — measured colours must never be presented as confirmed fact.
  brand_colors_detected?: boolean;
  logo_url?: string;
  /** The website's own version for dark backgrounds, when it has one (SGL-547). */
  logo_url_dark?: string;
}

/** A colour measured off the customer's rendered site, with the job it does.
 *
 * `source` separates a colour lifted straight off the page ("measured") from one
 * the backend computed to satisfy legibility ("derived") — see brand_palette.py.
 * Nothing here is ever invented; a derived value only ever fills the text role,
 * and only when nothing on the page was readable against its own background.
 */
export interface MeasuredBrandColor {
  role: "primary" | "text" | "background";
  hex: string;
  source: "measured" | "derived";
}

function unwrapProfilePayload(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  let obj = raw;
  if (typeof obj === "string") {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  if (typeof obj !== "object" || Array.isArray(obj)) return null;
  const r = obj as Record<string, unknown>;
  const nested = r.business_profile ?? r.profile ?? r.data;
  const inner =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : r;
  const looksLikeProfile =
    !!(inner.business_name ||
      inner.name ||
      inner.website_url ||
      inner.website ||
      inner.city);
  return looksLikeProfile ? inner : null;
}

export function extractProfileFromToolCall(toolData: {
  tool_name?: string;
  result?: unknown;
}): Record<string, unknown> | null {
  const toolName = String(toolData.tool_name || "").toLowerCase();
  if (
    toolName.includes("image") ||
    toolName.includes("generate_image") ||
    toolName.includes("social_posts")
  ) {
    return null;
  }
  return unwrapProfilePayload(toolData.result);
}

export function mapToolPayloadToBusinessProfile(
  toolProfile: Record<string, unknown> | null,
  assistantText: string
): OnboardingBusinessProfile {
  const src = toolProfile || {};
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = src[k];
      if (v != null && String(v).trim()) return String(v).trim();
    }
    return "";
  };
  const pickList = (...keys: string[]): string[] => {
    for (const k of keys) {
      const v = src[k];
      if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
      if (typeof v === "string" && v.trim()) return [v.trim()];
    }
    return [];
  };
  // Measured colours arrive with the role each one plays. Entries missing a role
  // or a hex are dropped rather than defaulted — a colour whose role we are
  // guessing at is exactly what this ticket exists to stop.
  const pickPalette = (...keys: string[]): MeasuredBrandColor[] => {
    for (const k of keys) {
      const v = src[k];
      if (!Array.isArray(v)) continue;
      const parsed = v.flatMap((entry): MeasuredBrandColor[] => {
        if (!entry || typeof entry !== "object") return [];
        const { role, hex, source } = entry as Record<string, unknown>;
        if (typeof role !== "string" || typeof hex !== "string") return [];
        if (!/^#[0-9a-f]{6}$/i.test(hex.trim())) return [];
        if (role !== "primary" && role !== "text" && role !== "background") return [];
        return [{
          role,
          hex: hex.trim().toLowerCase(),
          source: source === "derived" ? "derived" : "measured",
        }];
      });
      if (parsed.length) return parsed;
    }
    return [];
  };

  const summary =
    assistantText.trim() ||
    pick("message", "summary") ||
    "Here's what I found from your conversation.";

  const rawBusinessType = pick("business_type");
  const businessType =
    rawBusinessType === "SERVICE" || rawBusinessType === "PRODUCT" || rawBusinessType === "BOTH"
      ? (rawBusinessType as "SERVICE" | "PRODUCT" | "BOTH")
      : "";

  return {
    business_name: pick("business_name", "name"),
    website_url: pick("website_url", "website", "websiteUrl"),
    city: pick("city"),
    state: pick("state", "region"),
    country: pick("country") || "USA",
    industry: pick("industry", "business_type_label"),
    brand_voice: pick("brand_voice", "tone", "voice"),
    services: pickList("services", "services_offered", "offerings"),
    description: pick("description", "about", "summary_short"),
    message: summary,
    tagline: pick("tagline") || undefined,
    business_type: businessType || undefined,
    target_audience: pick("target_audience") || undefined,
    service_areas: pickList("service_areas", "serviceAreas"),
    phone_number: pick("phone_number", "phone") || undefined,
    contact_email: pick("contact_email", "email") || undefined,
    manager_email: pick("manager_email") || undefined,
    brand_colors: pickList("brand_colors", "brandColors") || undefined,
    // The roles the backend measured — identity / body text / page ground. Read
    // explicitly rather than inferred from list position (SGL-223): position was
    // being mapped onto primary/secondary/accent, which relabelled the measured
    // text colour as "secondary" and the page background as "accent".
    brand_palette: pickPalette("brand_palette", "brandPalette"),
    // Read off the payload, never inferred from "are there colours?" (SGL-223).
    // The backend sets this only when a screenshot measurement actually produced
    // a palette. Deriving it from brand_colors.length told a customer who had
    // typed their own colours that we had guessed them from their site.
    brand_colors_detected: src.brand_colors_detected === true,
    logo_url: pick("logo_url", "logoUrl") || undefined,
    logo_url_dark: pick("logo_url_dark", "logoUrlDark") || undefined,
  };
}

export type OnboardingStreamStatus =
  | { type: "idle" }
  | { type: "thinking" }
  | { type: "tool"; name: string }
  | { type: "generating" };

export interface CrawlProgressState {
  pagesCompleted: number;
  pagesTotal: number;
  latestPageTitle: string;
  status: "crawling" | "completed" | "timeout" | "failed";
}

/**
 * Calls POST /crawl/start directly — no AI agent involved.
 * The real customer_id is already known at this point so no target_customer_id needed.
 */
export async function startCrawlStream(options: {
  idToken: string;
  websiteUrl: string;
  customerId: string;
  onCrawlProgress?: (state: CrawlProgressState) => void;
  onComplete?: (result: unknown) => void;
  onError?: (err: string) => void;
}): Promise<void> {
  const { idToken, websiteUrl, customerId, onCrawlProgress, onComplete, onError } = options;

  const response = await authedFetch(
    `${CHAT_API_BASE_URL.replace(/\/$/, "")}/crawl/start`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ website_url: websiteUrl, customer_id: customerId, max_pages: 5 }),
    },
    idToken
  );

  if (!response.ok) {
    onError?.(`Crawl request failed: ${response.status}`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError?.("No response body from crawl stream");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.trim()) continue;

        if (line.startsWith("event: ")) {
          currentEvent = line.substring(7).trim();
          continue;
        }

        if (line.startsWith("data: ")) {
          try {
            const data = JSON.parse(line.substring(6));
            if (currentEvent === "crawl_progress") {
              onCrawlProgress?.({
                pagesCompleted: data.pages_completed ?? 0,
                pagesTotal: data.pages_total ?? 7,
                latestPageTitle: data.latest_page_title ?? "",
                status: data.status ?? "crawling",
              });
            } else if (currentEvent === "crawl_complete") {
              onComplete?.(data);
            } else if (currentEvent === "crawl_error") {
              onError?.(data.error ?? "Crawl failed");
            }
          } catch {
            debug.warn("Crawl stream: skipped unparsed line");
          }
          currentEvent = "";
        }
      }
    }
  } finally {
    reader.releaseLock?.();
  }
}

export async function consumeOnboardingChatStream(options: {
  idToken: string;
  agentId: string;
  customerId: string;
  provider: string;
  messages: OnboardingChatMessage[];
  setStreamStatus: (s: OnboardingStreamStatus) => void;
  setStreamingContent: (text: string) => void;
  onCrawlProgress?: (state: CrawlProgressState) => void;
  /** Each tool call's start and finish, for the step timeline. */
  onStep?: (event: { type: "start" | "end" | "error"; tool?: string }) => void;
}): Promise<{
  assistantText: string;
  mergedProfilePayload: Record<string, unknown> | null;
  streamError: ChatStreamError | null;
}> {
  const {
    idToken,
    agentId,
    customerId,
    provider,
    messages,
    setStreamStatus,
    setStreamingContent,
    onCrawlProgress,
    onStep,
  } = options;

  let accumulatedText = "";
  let mergedPatch: Record<string, unknown> | null = null;
  let streamError: ChatStreamError | null = null;

  const mergePatch = (patch: Record<string, unknown>) => {
    mergedPatch = { ...mergedPatch, ...patch };
  };

  const response = await sendStreamingChatMessage(
    {
      agent_id: agentId,
      provider,
      messages: messages.map((msg) => ({
        role: msg.role,
        content: [{ type: "input_text" as const, text: msg.content }],
      })),
      customer_id: customerId,
    },
    idToken
  );

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body from chat stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.trim()) continue;

        if (line.startsWith("event: ")) {
          currentEvent = line.substring(7).trim();
          continue;
        }

        if (line.startsWith("data: ")) {
          try {
            const jsonStr = line.substring(6);
            const data = JSON.parse(jsonStr);

            const result = handleStreamEvent(currentEvent, data, {
              onStart: () => setStreamStatus({ type: "idle" }),
              onThinking: () => setStreamStatus({ type: "thinking" }),
              onToolSelected: (toolName) => {
                if (toolName) onStep?.({ type: "start", tool: toolName });
                setStreamStatus({ type: "tool", name: toolName || "tool" });
              },
              onToolExecuting: (toolName) => {
                if (toolName) onStep?.({ type: "start", tool: toolName });
                setStreamStatus({ type: "tool", name: toolName || "tool" });
              },
              onBusinessProfile: (data) => {
                onStep?.({ type: "end", tool: "output_business_profile" });
                mergePatch(data);
                setStreamStatus({ type: "generating" });
              },
              onCrawlProgress: (pagesCompleted, pagesTotal, latestPageTitle, status) => {
                onCrawlProgress?.({
                  pagesCompleted,
                  pagesTotal,
                  latestPageTitle,
                  status: status as CrawlProgressState["status"],
                });
              },
              onToolCall: (toolData) => {
                // A website read that found nothing is a failed step, not a
                // tick: the tool reports it in its result, not as an error.
                const readFailed =
                  toolData?.tool_name === "fetch_website_profile" &&
                  !(unwrapProfilePayload(toolData?.result) || {}).business_name;
                onStep?.({ type: readFailed ? "error" : "end", tool: toolData?.tool_name });
                const patch = extractProfileFromToolCall(toolData);
                if (patch) mergePatch(patch);
                setStreamStatus({ type: "generating" });
              },
              onGenerating: () => setStreamStatus({ type: "generating" }),
              onContent: (delta, text) => {
                onStep?.({ type: "end" });
                setStreamStatus({ type: "generating" });
                if (delta) accumulatedText += delta;
                else if (text) accumulatedText = text;
                setStreamingContent(accumulatedText);
              },
              onContentWithActions: (content) => {
                setStreamStatus({ type: "generating" });
                accumulatedText = content;
                setStreamingContent(accumulatedText);
              },
              onEnd: () => {
                onStep?.({ type: "end" });
                setStreamStatus({ type: "idle" });
              },
              onError: (err) => {
                streamError = err;
              },
              onToolError: (toolName) => {
                onStep?.({ type: "error", tool: toolName });
                setStreamStatus({ type: "idle" });
              },
            });

            if (result.shouldReturn && result.error) {
              streamError = result.error;
            }

            currentEvent = "";
          } catch {
            const text = line.substring(6).trim();
            if (text) {
              accumulatedText += text;
              setStreamingContent(accumulatedText);
            }
          }
        } else {
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              accumulatedText += data.delta;
              setStreamingContent(accumulatedText);
            } else if (data.text || data.content) {
              accumulatedText = data.text || data.content;
              setStreamingContent(accumulatedText);
            }
          } catch {
            debug.warn("Onboarding stream: skipped unparsed line");
          }
        }
      }
    }
  } finally {
    reader.releaseLock?.();
  }

  setStreamStatus({ type: "idle" });
  return {
    assistantText: accumulatedText,
    mergedProfilePayload: mergedPatch,
    streamError,
  };
}

/**
 * Start the new Brand's starter knowledge on the server (SGL-461): the website
 * pages plus icp.md, services.md, faq.md and pricing.md, attached to every
 * agent. Answers at once (202); the work finishes on the server even if the
 * owner leaves, which the old in-browser crawl never did. Throws when the
 * server refuses, so the caller can log it loudly.
 */
export async function startStarterKnowledge(options: {
  idToken: string;
  customerId: string;
  agentIds: string[];
  websiteUrl?: string;
}): Promise<void> {
  const response = await authedFetch(
    `${CHAT_API_BASE_URL.replace(/\/$/, "")}/onboarding/starter-knowledge`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customer_id: options.customerId,
        agent_ids: options.agentIds,
        website_url: options.websiteUrl || null,
      }),
    },
    options.idToken
  );
  if (response.status !== 202 && !response.ok) {
    throw new Error(`Starter knowledge request failed: HTTP ${response.status}`);
  }
}

export type StarterStepStatus = "pending" | "running" | "done" | "error";
export interface StarterKnowledgeStatus {
  steps: {
    website?: { status: StarterStepStatus; pages?: number };
    files: { status: StarterStepStatus; files?: string[] };
    training: { status: StarterStepStatus; problems?: string[] };
  };
  done: boolean;
  ok: boolean;
}

/** Progress of the server-side starter knowledge; null until it has started. */
export async function getStarterKnowledgeStatus(
  idToken: string,
  customerId: string
): Promise<StarterKnowledgeStatus | null> {
  const response = await authedFetch(
    `${CHAT_API_BASE_URL.replace(/\/$/, "")}/onboarding/starter-knowledge/status?customer_id=${encodeURIComponent(customerId)}`,
    { method: "GET" },
    idToken
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Starter knowledge status failed: HTTP ${response.status}`);
  return (await response.json()) as StarterKnowledgeStatus;
}
